import IconLock from "@/components/icons/IconLock";
import image from "@/constants/image";
import { useRichTextHeight } from "@/helper/useRichTextHeight";
import useExtensions from "@/hooks/useExtensions";
import { Report } from "@/models/report";
import { TYPE_PAGE } from "@/utils/AppConfig";
import { formatDate } from "@/utils/formatNumber";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import IosShareOutlinedIcon from "@mui/icons-material/IosShareOutlined";
import { Box } from "@mui/material";
import clsx from "clsx";
import { motion } from "framer-motion";
import { RichTextReadOnly } from "mui-tiptap";
import { useTranslations } from "next-intl";
import Image from "next/image";
import React, {
  memo,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
interface ContentLeftProps {
  type: number;
  report?: Report;
  isLoggedIn?: boolean;
  isMobile?: boolean;
  lang: string;
  mode?: string;
  heightRightRef?: React.RefObject<HTMLDivElement | null>;
}

const ContentLeft = ({
  report,
  isLoggedIn,
  isMobile,
  lang,
  heightRightRef,
  type,
  mode,
}: ContentLeftProps) => {
  //hooks
  const t = useTranslations("messages");
  const extensions = useExtensions();
  // states
  // const [isContentReady, setIsContentReady] = useState<boolean>(false);
  const [shouldShowReadMore, setShouldShowReadMore] = useState<boolean>(false);
  const [isReadMore, setIsReadMore] = useState<boolean>(true);

  //refs
  const heightheaderRef = useRef<HTMLDivElement | null>(null);
  const richTextRef = useRef<HTMLDivElement>(null);
  const textCKEditor = useRef<HTMLDivElement | null>(null);
  const titleRef = useRef<HTMLDivElement>(null);

  const { height: richTextHeight, isReady: isRichTextReady } =
    useRichTextHeight(
      richTextRef,
      report?.description,
      [isMobile] // dependencies
    );
  useEffect(() => {
    if (typeof window !== "undefined" && isReadMore && titleRef.current) {
      titleRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [isReadMore]);

  useLayoutEffect(() => {
    if (
      !isRichTextReady ||
      !heightRightRef?.current ||
      !heightheaderRef.current
    ) {
      return;
    }

    const rightHeight = heightRightRef.current.clientHeight;
    const headerHeight = heightheaderRef.current.clientHeight;
    const availableHeight = rightHeight - headerHeight - 16;

    setShouldShowReadMore(richTextHeight > availableHeight);
  }, [isRichTextReady, richTextHeight, isMobile]);

  const handleReadMore = () => {
    setIsReadMore(false);
  };

  const handleRenderTitle = (type: number, mode: string) => {
    if (mode === "powerbi") {
      return (
        <Box className="gap-2 flex items-center">
          <Image src={image.icDouble} alt="icDouble" height={24} />
          <span
            className="!font-medium !text-base/[100%] text-gray_80 m-0"
            translation-key="report_detail_interactive_dashboard"
          >
            {t("report_detail_interactive_dashboard")}
          </span>
        </Box>
      );
    }
    switch (type) {
      case TYPE_PAGE.DETAIL:
        return (
          <Box className="gap-2 flex items-center">
            <Image src={image.iconPDF} alt="iconPDF" height={24} />
            <span
              className="!font-medium !text-base/[100%] text-gray_80 m-0"
              translation-key="report_detail_pdf"
            >
              {t("report_detail_pdf", {
                number: (report?.reportAttachments && report?.pageCount) || 0,
              })}
            </span>
          </Box>
        );
      case TYPE_PAGE.INTERACTIVE_SHARE:
        return (
          <Box className="gap-2 flex items-center">
            <Image src={image.icDouble} alt="icDouble" height={24} />
            <span
              className="!font-medium !text-base/[100%] text-gray_80 m-0"
              translation-key="report_detail_interactive_dashboard"
            >
              {t("report_detail_interactive_dashboard")}
            </span>
          </Box>
        );
      case TYPE_PAGE.DETAIL_SHARE:
        return (
          <Box className="gap-2 flex items-center">
            <Box className="gap-2 flex items-center">
              {report?.isPrivate ? (
                <IconLock className="!w-4 !h-4 text-gray_80" />
              ) : (
                <IosShareOutlinedIcon className="!w-4 !h-4 text-gray_80" />
              )}
              <span
                className="!font-medium !text-base/[100%] text-gray_80 m-0"
                style={{
                  fontFamily: "Montserrat",
                  fontWeight: 500,
                  fontSize: "16px",
                  lineHeight: "24px",
                  letterSpacing: "0%",
                }}
                translation-key={
                  report?.isPrivate
                    ? "report_detail_protected_content_shared"
                    : "report_detail_content_shared"
                }
              >
                {/* share name */}
                {report?.isPrivate
                  ? t("report_detail_protected_content_shared")
                  : t("report_detail_content_shared")}

                <span
                  style={{
                    fontFamily: "Montserrat",
                    fontWeight: 700,
                    fontSize: "16px",
                    lineHeight: "24px",
                    letterSpacing: "0%",
                  }}
                >
                  {report?.nameUserShared}
                </span>
              </span>
            </Box>
          </Box>
        );
      default:
        return (
          <Box className="gap-2 flex items-center">
            <Image src={image.iconPDF} alt="iconPDF" height={24} />
            <span
              className="!font-medium !text-base/[100%] text-gray_80 m-0"
              translation-key="report_detail_pdf"
            >
              {t("report_detail_pdf", {
                number: (report?.reportAttachments && report?.pageCount) || 0,
              })}
            </span>
          </Box>
        );
    }
  };

  return (
    <Box className="xl:max-w-[800px] w-full relative">
      <Box className={`border-b-[1px] gap-4 grid ${isReadMore && "relative"}`}>
        <Box className="grid gap-4" ref={heightheaderRef}>
          <Box className="gap-2 grid">
            {handleRenderTitle(type, mode || "")}
            <h1
              className="!font-bold !text-3xl/9 sm:!text-4xl/[52px] text-ciBlue_900 m-0"
              ref={titleRef}
            >
              {report?.title}
            </h1>
          </Box>
          <Box>
            <p
              className="m-0 font-medium text-base/6 text-gray_80 flex items-center justify-start gap-1"
              translation-key="report_detail_published_on"
              suppressHydrationWarning
            >
              <AccessTimeIcon
                className="bg-ciBlue_900 rounded-full text-white !w-[18px] !h-[18px]"
                height={24}
                width={24}
              />
              {t("report_detail_published_on", {
                date: formatDate(
                  String(report?.publishedDate || report?.createdAt),
                  lang
                ),
              })}
            </p>
          </Box>
          {/* <Box className="gap-6 grid">
                        {dataDetail?.topics && dataDetail?.topics?.length > 0 && (
                            <Box className="gap-[12px_16px] flex flex-wrap">
                                {dataDetail?.topics?.map((item: any, index: any) => (
                                    <Button
                                        key={index}
                                        variant="text"
                                        className="gap-[10px] !bg-gray_5 !py-1 !px-3 !rounded-[50px]"
                                    >
                                        <span className="text-gray_60 !font-medium !text-sm/6 normal-case">
                                            {item?.name}
                                        </span>
                                    </Button>
                                ))}
                            </Box>
                        )}
                    </Box> */}
        </Box>
        <Box className="grid">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: isRichTextReady ? 1 : 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <Box
              className={clsx(`gap-5 grid`, {
                "line-clamp-3": isReadMore && shouldShowReadMore,
                "overflow-hidden": isReadMore && shouldShowReadMore,
                relative: isReadMore && shouldShowReadMore,
              })}
              sx={{
                height:
                  isReadMore && shouldShowReadMore
                    ? `${(heightRightRef?.current?.clientHeight || 0) - (heightheaderRef.current?.clientHeight || 0) - 16}px`
                    : "auto",
                minHeight: 100, // Đảm bảo luôn có chiều cao tối thiểu
                maxHeight: isRichTextReady ? "none" : "100px",
                overflow:
                  isReadMore && shouldShowReadMore ? "hidden" : "visible",
                opacity: isRichTextReady ? 1 : 0,
                transition: "opacity 0.2s ease-in-out",
              }}
              ref={textCKEditor}
            >
              {report?.description && (
                <div className="custom-rich-text" ref={richTextRef}>
                  <RichTextReadOnly
                    immediatelyRender={false}
                    injectCSS={true}
                    content={report?.description ?? ""}
                    extensions={extensions}
                  />
                </div>
              )}
              {isReadMore && shouldShowReadMore && (
                <Box
                  className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex items-end"
                  sx={{
                    height: "64px",
                    background:
                      "linear-gradient(to top, #fff 50%, rgba(255,255,255,0.7) 60%, rgba(255,255,255,0) 100%)",
                    pointerEvents: "none",
                  }}
                >
                  <span
                    className="font-semibold text-base/6 !text-ciBlue_900 hover:underline cursor-pointer"
                    onClick={handleReadMore}
                    style={{
                      pointerEvents: "auto",
                      position: "absolute",
                      left: 0,
                      bottom: 0,
                      background: "#fff",
                      padding: "0 4px",
                    }}
                    translation-key="common_read_more"
                  >
                    {t("common_read_more")}
                  </span>
                </Box>
              )}

              {!isReadMore && shouldShowReadMore && (
                <span
                  className="font-semibold text-base/6 !text-ciBlue_900 hover:underline cursor-pointer"
                  onClick={() => setIsReadMore(true)}
                  translation-key="common_show_less"
                >
                  {t("common_show_less")}
                </span>
              )}
            </Box>
          </motion.div>
        </Box>
      </Box>
    </Box>
  );
};

export default memo(ContentLeft);
